import { API_BASE } from "./config";

// ── In-memory access token store ──────────────────────────────────────────────
// Kept in JS closure so it's inaccessible to injected scripts.
let _accessToken = null;

export function setAccessToken(token) {
  _accessToken = token;
}
export function getAccessToken() {
  return _accessToken;
}
export function clearAccessToken() {
  _accessToken = null;
}

// ── Token refresh ─────────────────────────────────────────────────────────────
let _refreshPromise = null;

async function refreshAccessToken() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = fetch(`${API_BASE}/api/auth/refresh`, {
    method: "POST",
    credentials: "include", // sends the httpOnly rq_refresh cookie
  })
    .then(async (r) => {
      if (!r.ok) throw new Error("refresh_failed");
      const data = await r.json();
      if (data.accessToken) setAccessToken(data.accessToken);
      return data.accessToken;
    })
    .finally(() => {
      _refreshPromise = null;
    });
  return _refreshPromise;
}

// ── Try to restore session on app load ────────────────────────────────────────
// Calls the refresh endpoint; if the cookie is present and valid a new access
// token is issued and stored. Returns user info from the token or null.
export async function tryRestoreSession() {
  try {
    const token = await refreshAccessToken();
    if (!token) return null;
    // Decode (no signature verification needed — server already did that)
    const payload = JSON.parse(atob(token.split(".")[1]));
    return { userId: Number(payload.sub), isAdmin: !!payload.isAdmin };
  } catch {
    return null;
  }
}

// ── apiFetch ──────────────────────────────────────────────────────────────────
// Drop-in replacement for fetch() that:
//   - Prepends API_BASE to relative URLs
//   - Attaches Authorization: Bearer header
//   - On 401, refreshes the access token once and retries
//   - Passes credentials: 'include' so the httpOnly cookie travels with requests
export default async function apiFetch(url, options = {}) {
  const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`;

  const buildHeaders = () => ({
    ...options.headers,
    ...(_accessToken ? { Authorization: `Bearer ${_accessToken}` } : {}),
  });


  const doFetch = (headers) =>
    fetch(fullUrl, { ...options, headers, credentials: "include" });

  let res = await doFetch(buildHeaders());

  if (res.status === 401) {
    try {
      await refreshAccessToken();
    } catch {
      return res; // refresh failed — let caller handle
    }
    res = await doFetch(buildHeaders());
  }

  return res;
}
